import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Eye, Star, MapPin, Car } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { ROUTES, buildRoute } from "@/constants/routes";
import { useDrivers, useDriverStates } from "../hooks/useDrivers";
import type { DriversParams } from "../api/drivers.api";
import { useFilters } from "@/hooks/useFilters";
import { usePagination } from "@/hooks/usePagination";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { FilterBar } from "@/components/common/FilterBar";
import {
  DriverStatusBadge,
  KYCStatusBadge,
} from "@/components/common/StatusBadge";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dropdown, DropdownItem } from "@/components/ui/dropdown";
import { TablePagination } from "@/components/ui/pagination";
import type { PendingDriverAPIResponse } from "@/types";

// "Status" is the account state (suspend/activate + KYC gate), distinct from
// the driver's live availability_status (online/offline/busy) shown as the
// green dot next to their avatar. There's no backend concept of "inactive"
// separate from suspended/pending, so it isn't offered here.
const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "pending_approval", label: "Pending Approval" },
];

// Matches the backend's VerificationStatus enum (driver-profile.entity.ts).
const kycOptions = [
  { value: "all", label: "All KYC Status" },
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
];

// Matches the backend's VehicleType enum (vehicle.entity.ts).
const vehicleOptions = [
  { value: "all", label: "All Vehicles" },
  { value: "bike", label: "Bike" },
  { value: "scooty", label: "Scooty" },
  { value: "auto", label: "Auto" },
  { value: "pickup", label: "Pickup" },
  { value: "mini_truck", label: "Mini Truck" },
  { value: "three_wheeler", label: "Three Wheeler" },
  { value: "tata_ace", label: "Tata Ace" },
  { value: "pickup_8ft", label: "Pickup 8ft" },
  { value: "tata_407", label: "Tata 407" },
];
const VEHICLE_LABEL: Record<string, string> = Object.fromEntries(
  vehicleOptions.filter((v) => v.value !== "all").map((v) => [v.value, v.label]),
);

/** Computed admin-facing status — see the backend comment in getAllDrivers. */
function computeDriverStatus(driver: PendingDriverAPIResponse): "active" | "suspended" | "pending_approval" {
  if (driver.user?.is_active === false) return "suspended";
  if (driver.verification_status !== "approved") return "pending_approval";
  return "active";
}

export function DriversListPage() {
  const navigate = useNavigate();
  const { page, limit, setPage, setLimit } = usePagination();
  const { filters, setFilter, resetFilters } = useFilters({
    defaultValues: {
      search: "",
      status: "all",
      kycStatus: "all",
      vehicleType: "all",
      state: "all",
    },
  });

  const { data: states } = useDriverStates();
  const stateOptions = [
    { value: "all", label: "All States" },
    ...(states || []).map((s) => ({ value: s.state, label: `${s.state} (${s.count})` })),
  ];

  const { data, isLoading, isError, error, refetch } = useDrivers({
    search: filters.search,
    status: filters.status as
      | "all"
      | "active"
      | "suspended"
      | "pending_approval"
      | undefined,
    kycStatus: filters.kycStatus as
      | "all"
      | "approved"
      | "pending"
      | "rejected"
      | undefined,
    vehicleType: filters.vehicleType as DriversParams["vehicleType"],
    state: filters.state,
    page,
    limit,
  });

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Drivers"
          description="Manage driver accounts, verify KYC, and track performance"
          actions={
            <Button onClick={() => navigate(ROUTES.KYC_PENDING)}>
              Review Pending KYC
            </Button>
          }
        />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">Failed to load drivers</h3>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            {(error as Error)?.message ||
              "The server returned an error. This may be a temporary issue."}
          </p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  const columns: ColumnDef<PendingDriverAPIResponse>[] = [
    {
      accessorKey: "user.name",
      header: "Driver",
      cell: ({ row }) => {
        const user = row.original.user;
        const name = user?.name || "Unknown";
        const phone = user?.phone || "Unknown";
        return (
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar src={row.original.profile_image} alt={name} size="sm" />
              {row.original.availability_status === "online" && (
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-success border-2 border-background" />
              )}
            </div>
            <div>
              <p className="font-medium">{name}</p>
              <p className="text-sm text-muted-foreground">{phone}</p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "vehicle",
      header: "Vehicle",
      cell: ({ row }) => {
        const vehicle = row.original.vehicle;
        return (
          <div className="flex items-center gap-2">
            <Car className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                {vehicle ? (VEHICLE_LABEL[vehicle.vehicle_type ?? ""] ?? vehicle.vehicle_type ?? "Assigned") : "Not Assigned"}
              </p>
              {vehicle?.registration_number && (
                <p className="text-xs text-muted-foreground">{vehicle.registration_number}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "average_rating",
      header: "Rating",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Star className="h-4 w-4 text-warning fill-warning" />
          <span>
            {row.original.average_rating
              ? Number(row.original.average_rating).toFixed(1)
              : "N/A"}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "total_trips",
      header: "Trips",
      cell: ({ row }) => row.original.total_trips || 0,
    },
    {
      accessorKey: "wallet_balance",
      header: "Wallet",
      cell: ({ row }) =>
        formatCurrency(Number(row.original.wallet_balance || 0)),
    },
    {
      accessorKey: "user.is_active",
      header: "Status",
      cell: ({ row }) => (
        <DriverStatusBadge status={computeDriverStatus(row.original)} />
      ),
    },
    {
      accessorKey: "verification_status",
      header: "KYC",
      cell: ({ row }) => (
        <KYCStatusBadge status={row.original.verification_status as any} />
      ),
    },
    {
      accessorKey: "address",
      header: "City",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {row.original.address || "N/A"}
        </div>
      ),
    },
    {
      accessorKey: "user.state",
      header: "State",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          {row.original.user?.state || <span className="text-muted-foreground">Unknown</span>}
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button variant="ghost" size="icon-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          }
        >
          <DropdownItem
            icon={<Eye className="h-4 w-4" />}
            onClick={() =>
              navigate(
                buildRoute(ROUTES.DRIVER_DETAIL, { id: row.original.id }),
                { state: { driver: row.original } },
              )
            }
          >
            View Details
          </DropdownItem>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Drivers"
        description="Manage driver accounts, verify KYC, and track performance"
        actions={
          <Button onClick={() => navigate(ROUTES.KYC_PENDING)}>
            Review Pending KYC
          </Button>
        }
      />

      <FilterBar
        filters={[
          {
            type: "search",
            key: "search",
            placeholder: "Search by name, phone, or vehicle...",
            className: "w-80",
          },
          {
            type: "select",
            key: "status",
            placeholder: "Status",
            options: statusOptions,
          },
          {
            type: "select",
            key: "kycStatus",
            placeholder: "KYC Status",
            options: kycOptions,
          },
          {
            type: "select",
            key: "vehicleType",
            placeholder: "Vehicle",
            options: vehicleOptions,
          },
          {
            type: "select",
            key: "state",
            placeholder: "State",
            options: stateOptions,
          },
        ]}
        values={filters}
        onChange={(key, value) =>
          setFilter(key as keyof typeof filters, value as string)
        }
        onReset={resetFilters}
        onRefresh={() => refetch()}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) =>
          navigate(buildRoute(ROUTES.DRIVER_DETAIL, { id: row.id }), {
            state: { driver: row },
          })
        }
      />

      {data && (
        <TablePagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
          totalItems={data.pagination.total}
          pageSize={data.pagination.limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
      )}
    </div>
  );
}
